import { useLocalStorage } from '@mantine/hooks';
import ApartmentIcon from '@mui/icons-material/Apartment';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import GroupIcon from '@mui/icons-material/Group';
import InfoIcon from '@mui/icons-material/Info';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import PersonIcon from '@mui/icons-material/Person';
import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    Badge,
    Box,
    Divider,
    Grid,
    List,
    ListItem,
    ListItemIcon,
    ListItemText,
    Menu,
    MenuItem,
    Stack,
    Switch,
    Tooltip,
    Typography,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useState } from 'react';

import { MenuItemProps } from '.';
import NavigationLink from '../../../Link/NavigationLink';

interface RequestBreakdown {
    company: number;
    group: number;
    instance: number;
}

/**
 * Profile Menu props interface
 */
interface ProfileMenuContentProps {
    admin?: boolean;
    anchorElUser: HTMLElement | null;
    avatarComponent?: React.ReactNode;

    // User display props
    fullName?: string;
    handleCloseUserMenu: () => void;
    // Optional access control
    hasAdminAccess?: boolean;

    // Optional feature flags
    hasNext?: boolean;
    menuItems: Record<string, MenuItemProps[]>;
    // Optional features
    remainingTimeStatus?: React.ReactNode;

    requestBreakdown?: RequestBreakdown;

    totalRequests?: number;
    userTitle?: string;
}

/**
 * Profile Menu content that can be used across different projects
 */
export function ProfileMenuContent({
    admin,
    anchorElUser,
    avatarComponent,
    fullName,
    handleCloseUserMenu,
    hasAdminAccess = false,
    hasNext = true,
    menuItems,
    remainingTimeStatus,
    requestBreakdown,
    totalRequests = 0,
    userTitle,
}: Readonly<ProfileMenuContentProps>) {
    const theme = useTheme();
    const isSmall = theme.breakpoints.down('sm');

    const [showInfoButton, setShowInfoButton] = useLocalStorage({ defaultValue: true, key: 'showInfoButton' });

    const handleInfoButtonToggle = () => {
        setShowInfoButton(!showInfoButton);
    };

    const [expanded, setExpanded] = useState(false);

    const handleAccordionToggle = () => {
        setExpanded((prev) => !prev);
    };

    // Calculate total requests count
    const totalRequestsCount = requestBreakdown
        ? requestBreakdown.company + requestBreakdown.group + requestBreakdown.instance
        : totalRequests;

    // Component to render request breakdown
    const renderRequestBreakdown = () => {
        if (!requestBreakdown || totalRequestsCount === 0) {
            return null;
        }

        return (
            <List dense>
                {requestBreakdown.company > 0 && (
                    <ListItem>
                        <ListItemIcon>
                            <ApartmentIcon fontSize="small" />
                        </ListItemIcon>
                        <ListItemText primary={`Company Requests: ${requestBreakdown.company.toString()}`} />
                    </ListItem>
                )}
                {requestBreakdown.group > 0 && (
                    <ListItem>
                        <ListItemIcon>
                            <GroupIcon fontSize="small" />
                        </ListItemIcon>
                        <ListItemText primary={`Group Requests: ${requestBreakdown.group.toString()}`} />
                    </ListItem>
                )}
                {requestBreakdown.instance > 0 && (
                    <ListItem>
                        <ListItemIcon>
                            <LocationOnIcon fontSize="small" />
                        </ListItemIcon>
                        <ListItemText primary={`Instance Requests: ${requestBreakdown.instance.toString()}`} />
                    </ListItem>
                )}
            </List>
        );
    };

    return (
        <Menu
            anchorEl={anchorElUser}
            anchorOrigin={{
                horizontal: 'left',
                vertical: 'bottom',
            }}
            onClose={handleCloseUserMenu}
            open={Boolean(anchorElUser)}
            slotProps={{
                list: {
                    sx: {
                        '::-webkit-scrollbar': {
                            width: '9px',
                        },
                        '::-webkit-scrollbar-thumb': {
                            background: 'whitesmoke',
                        },
                        '::-webkit-scrollbar-track': {
                            background: 'transparent',
                            marginBlock: '28px',
                        },
                        maxHeight: { sm: '75vh', xs: '60vh' },
                        overflow: 'auto',
                        p: 0,
                        scrollbarWidth: isSmall ? 'thin' : 'none',
                    },
                },

                paper: {
                    sx: {
                        backgroundColor: 'primary.main',
                        borderRadius: 8,
                        display: { lg: 'block', xs: 'none' },
                        width: 320,
                    },
                },
            }}
            transformOrigin={{
                horizontal: 250,
                vertical: 'top',
            }}
        >
            <Stack
                divider={<Divider sx={{ borderColor: '' }} />}
                sx={{ backgroundColor: '#edf2f6', borderRadius: 7.5 }}
            >
                <Stack
                    direction="row"
                    sx={{
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        pb: 1,
                        pt: 2,
                        px: 2,
                    }}
                >
                    <Stack
                        direction="row"
                        spacing={1}
                        sx={{
                            alignItems: 'center',
                        }}
                    >
                        {avatarComponent || (
                            <PersonIcon
                                sx={{
                                    color: 'primary.main',
                                    flexShrink: 0,
                                    height: 40,
                                    width: 40,
                                }}
                            />
                        )}

                        <ListItemText primary={fullName} secondary={userTitle} sx={{ ml: 1 }} />
                    </Stack>
                    <Stack
                        sx={{
                            alignItems: 'center',
                        }}
                    >
                        <Tooltip placement="top" title="Display additional info">
                            <InfoIcon sx={{ color: showInfoButton ? 'mlBlue.main' : 'gray', pl: 0.35 }} />
                        </Tooltip>
                        <Switch checked={showInfoButton} color="info" onChange={handleInfoButtonToggle} size="small" />
                    </Stack>
                </Stack>

                <Stack
                    direction="column"
                    sx={{
                        pb: 1,
                        px: 2,
                    }}
                >
                    {/* Request Breakdown Section */}
                    {!admin && requestBreakdown && totalRequestsCount > 0 ? (
                        <Grid
                            container
                            sx={{
                                alignItems: 'center',
                                pl: 1,
                                pt: 1,
                            }}
                        >
                            <Grid size={12}>
                                <Typography
                                    color="primary"
                                    sx={{
                                        fontWeight: 'bold',
                                        lineHeight: 2,
                                    }}
                                    variant="overline"
                                >
                                    Pending Requests ({totalRequestsCount})
                                </Typography>
                            </Grid>
                            <Grid size={12}>
                                <Box sx={{ pl: 1 }}>{renderRequestBreakdown()}</Box>
                            </Grid>
                        </Grid>
                    ) : null}

                    {/* Existing menu sections */}
                    {Object.entries(menuItems).map(([category, items]) => {
                        return (
                            <Grid
                                container
                                key={category}
                                sx={{
                                    alignItems: 'center',
                                    pl: 1,
                                    pt: category !== 'Status' ? 1 : 0,
                                }}
                            >
                                <Grid size={12}>
                                    {category === 'Status' && remainingTimeStatus ? (
                                        <Stack
                                            direction="row"
                                            spacing={1}
                                            sx={{
                                                justifyContent: 'space-between',
                                            }}
                                        >
                                            <Typography
                                                color="primary"
                                                sx={{
                                                    fontWeight: 'bold',
                                                    lineHeight: 2,
                                                }}
                                                variant="overline"
                                            >
                                                {category}
                                            </Typography>
                                            {remainingTimeStatus}
                                        </Stack>
                                    ) : category === 'Help' ? (
                                        <Accordion
                                            disableGutters
                                            elevation={0}
                                            expanded={expanded}
                                            onChange={handleAccordionToggle}
                                            sx={{
                                                '&:before': {
                                                    display: 'none',
                                                },
                                                backgroundColor: 'transparent',
                                            }}
                                        >
                                            <AccordionSummary expandIcon={null} sx={{ padding: 0 }}>
                                                <Typography
                                                    color="primary"
                                                    sx={{
                                                        fontWeight: 'bold',
                                                        lineHeight: 2,
                                                    }}
                                                    variant="overline"
                                                >
                                                    {category}
                                                </Typography>
                                                <ExpandMoreIcon
                                                    fontSize="small"
                                                    sx={{
                                                        mt: 0.15,
                                                        transform: expanded ? 'rotate(0deg)' : 'rotate(-90deg)',
                                                        transition: 'transform 0.2s',
                                                    }}
                                                />
                                            </AccordionSummary>
                                            <AccordionDetails sx={{ padding: 0 }}>
                                                <Grid columnSpacing={1} container>
                                                    {items.map((item) => {
                                                        if (item.adminAccess && !hasAdminAccess) {
                                                            return null;
                                                        }

                                                        const menuItem = (
                                                            <Badge
                                                                badgeContent={item.totalRequests ? totalRequests : 0}
                                                                color="warning"
                                                                key={item.label}
                                                                overlap="circular"
                                                            >
                                                                <MenuItem
                                                                    onClick={item.handleClick}
                                                                    sx={{
                                                                        alignItems: 'flex-start',
                                                                        borderRadius: 4,
                                                                        display: 'flex',
                                                                        justifyContent: 'center',
                                                                        width: 88,
                                                                    }}
                                                                >
                                                                    <Stack
                                                                        sx={{
                                                                            py: 1,
                                                                        }}
                                                                    >
                                                                        {item.icon ? (
                                                                            <ListItemIcon
                                                                                sx={{
                                                                                    alignItems: 'center',
                                                                                    display: 'flex',
                                                                                    justifyContent: 'center',
                                                                                }}
                                                                            >
                                                                                {item.icon}
                                                                            </ListItemIcon>
                                                                        ) : null}
                                                                        <ListItemText
                                                                            primary={item.label}
                                                                            slotProps={{
                                                                                primary: {
                                                                                    sx: {
                                                                                        lineHeight: 1.1,
                                                                                        textAlign: 'center',
                                                                                        whiteSpace: 'normal',
                                                                                    },
                                                                                },
                                                                            }}
                                                                        />
                                                                    </Stack>
                                                                </MenuItem>
                                                            </Badge>
                                                        );

                                                        return (
                                                            <Grid key={item.label} size="auto" sx={{ display: 'flex' }}>
                                                                {item.to ? (
                                                                    <NavigationLink admin={item.admin} to={item.to}>
                                                                        {menuItem}
                                                                    </NavigationLink>
                                                                ) : (
                                                                    menuItem
                                                                )}
                                                            </Grid>
                                                        );
                                                    })}
                                                </Grid>
                                            </AccordionDetails>
                                        </Accordion>
                                    ) : (
                                        <Typography
                                            color="primary"
                                            sx={{
                                                fontWeight: 'bold',
                                                lineHeight: 2,
                                            }}
                                            variant="overline"
                                        >
                                            {category}
                                        </Typography>
                                    )}
                                </Grid>
                                {category !== 'Help' && (
                                    <Grid columnSpacing={1} container>
                                        {items.map((item) => {
                                            if (item.adminAccess && !hasAdminAccess) {
                                                return null;
                                            }

                                            const menuItem = (
                                                <Badge
                                                    badgeContent={item.totalRequests ? totalRequests : 0}
                                                    color="warning"
                                                    key={item.label}
                                                    overlap="circular"
                                                >
                                                    <MenuItem
                                                        onClick={item.handleClick}
                                                        sx={{
                                                            alignItems: 'flex-start',
                                                            borderRadius: 4,
                                                            display: 'flex',
                                                            justifyContent: 'center',
                                                            width: 88,
                                                        }}
                                                    >
                                                        <Stack
                                                            sx={{
                                                                py: 1,
                                                            }}
                                                        >
                                                            {item.icon ? (
                                                                <ListItemIcon
                                                                    sx={{
                                                                        alignItems: 'center',
                                                                        display: 'flex',
                                                                        justifyContent: 'center',
                                                                    }}
                                                                >
                                                                    {item.icon}
                                                                </ListItemIcon>
                                                            ) : null}
                                                            <ListItemText
                                                                primary={item.label}
                                                                slotProps={{
                                                                    primary: {
                                                                        sx: {
                                                                            lineHeight: 1.1,
                                                                            textAlign: 'center',
                                                                            whiteSpace: 'normal',
                                                                        },
                                                                    },
                                                                }}
                                                            />
                                                        </Stack>
                                                    </MenuItem>
                                                </Badge>
                                            );

                                            return (
                                                <Grid key={item.label} size="auto" sx={{ display: 'flex' }}>
                                                    {item.to ? (
                                                        <NavigationLink admin={item.admin} to={item.to}>
                                                            {menuItem}
                                                        </NavigationLink>
                                                    ) : (
                                                        menuItem
                                                    )}
                                                </Grid>
                                            );
                                        })}
                                    </Grid>
                                )}
                            </Grid>
                        );
                    })}
                </Stack>
            </Stack>
        </Menu>
    );
}
